import React, { MouseEvent, useEffect, useRef, useState } from 'react';

import {
  COMPONENT_SIZE_LARGE,
  COMPONENT_SIZE_MEDIUM,
  COMPONENT_SIZE_SMALL,
  ComponentSize,
} from '../../../typings/design-tokens';
import { makeClassNames } from '../../../utils/styles';
import IconArrowDown from '../../atoms/icons/icon-arrow-down';
import IconCalendar from '../../atoms/icons/icon-calendar';
import MenuItem from '../../atoms/menu-item/menu-item';
import './menu-list.scss';

// Use generaic design tokens
export const menuSizeList: ComponentSize[] = [COMPONENT_SIZE_SMALL, COMPONENT_SIZE_MEDIUM, COMPONENT_SIZE_LARGE];
export const menuVariantList: MenuVariant[] = ['DROPDOWN', 'CALENDAR'];

export interface IMenuListLink {
  label: string;
  href?: string;
  isSelected?: boolean;
}

type MenuVariant = 'DROPDOWN' | 'CALENDAR';

export interface IMenuList {
  links: IMenuListLink[];
  text: string;
  title?: string;
  showSelected?: boolean;
  menuSize?: ComponentSize;
  onClick?: (event: MouseEvent, id: number) => any;
  variant?: MenuVariant;
  analyticsDataAttribute?: string;
}

const classNames = (menuSize: string, isMenuVisible: boolean, variant: MenuVariant) =>
  makeClassNames({
    menuList: [isMenuVisible ? 'menu-list--opened' : 'menu-list--closed', menuSize ? `menu-list--${menuSize}` : ''],
    menuButton: [variant === 'CALENDAR' ? 'menu-list__button--calendar' : 'menu-list__button'],
    menuTitle: 'menu-list__title',
    menuButtonTitle: 'menu-list__button__p',
    menuButtonIcon: 'menu-list__button__arrow',
    menuDropdown: [variant === 'CALENDAR' ? ['menu-list__menu--calendar', 'menu-list__menu'] : 'menu-list__menu'],
  });

const MenuList = ({
  /* istanbul ignore next: default value on mandatory field */ links = [],
  /* istanbul ignore next: default value on mandatory field */ onClick = () => {},
  /* istanbul ignore next: default value on mandatory field */ showSelected = false,
  /* istanbul ignore next: default value on mandatory field */ menuSize = COMPONENT_SIZE_SMALL,
  /* istanbul ignore next: default value on mandatory field */ variant = 'DROPDOWN',
  text,
  title,
  analyticsDataAttribute,
}: IMenuList) => {
  // INTERNAL STATE for MENU SHOW/HIDE
  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const [isOnMouseEnterLastEvent, setIsOnMouseEnterLastEvent] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const itemsWrapperRef = useRef<HTMLDivElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const toggleMenuVisible = (forcedMenuVisible?: boolean) => setMenuVisible(forcedMenuVisible ?? !isMenuVisible);
  const onMenuItemClick = (event: MouseEvent, selectedIndex: number) => {
    toggleMenuVisible();
    onClick(event, selectedIndex);
    openButtonRef.current?.focus();
  };

  const cn = classNames(menuSize, isMenuVisible, variant);

  const openMenu = () => {
    setIsOnMouseEnterLastEvent(true);
    toggleMenuVisible(true);
    setFocusedIndex(-1);
  };

  const closeMenu = () => {
    setIsOnMouseEnterLastEvent(false);
    toggleMenuVisible(false);
    setFocusedIndex(-1);
  };

  const handleButtonKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isMenuVisible) {
      closeMenu();
      openButtonRef.current?.focus();
    }
    if (
      (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') &&
      !isMenuVisible
    ) {
      event.preventDefault();
      openMenu();
    }
  };

  useEffect(() => {
    const handleItemsKeyDown = (event: KeyboardEvent) => {
      if (!isMenuVisible && document.activeElement === itemsWrapperRef.current) {
        return;
      }

      if (event.key === 'Escape') {
        closeMenu();
        openButtonRef.current?.focus();
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();

        if (focusedIndex < links.length - 1) {
          setFocusedIndex(focusedIndex + 1);
        }
        if (focusedIndex === links.length - 1) {
          setFocusedIndex(0);
        }
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();

        if (focusedIndex > 0) {
          setFocusedIndex(focusedIndex - 1);
        }
        if (focusedIndex === 0) {
          setFocusedIndex(links.length - 1);
        }
      }
    };

    window.addEventListener('keydown', handleItemsKeyDown);

    return () => {
      window.removeEventListener('keydown', handleItemsKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuVisible, focusedIndex, links.length]);

  useEffect(() => {
    if (!isMenuVisible) {
      return;
    }
    (itemsWrapperRef.current?.children as HTMLCollectionOf<HTMLButtonElement>)[focusedIndex]?.focus();
  }, [focusedIndex, isMenuVisible]);

  // useEffect(() => {
  //   if (isMenuVisible) {
  //     document.body.style.overflow = 'hidden';
  //   } else {
  //     document.body.style.overflow = 'auto';
  //   }
  // }, [isMenuVisible]);

  return (
    <div
      className={cn.menuList}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
      data-analytics-element={analyticsDataAttribute}
    >
      <div
        onClick={() => {
          if (!isOnMouseEnterLastEvent) {
            toggleMenuVisible();
          }
          setIsOnMouseEnterLastEvent(false);
        }}
      >
        {variant === 'DROPDOWN' && title && <p className={cn.menuTitle}>{title}</p>}
        <button
          type="button"
          className={cn.menuButton}
          onKeyDown={handleButtonKeyDown}
          ref={openButtonRef}
          aria-expanded={isMenuVisible}
        >
          <span className={cn.menuButtonTitle}>{text}</span>
          <div className={cn.menuButtonIcon} aria-hidden>
            {variant === 'CALENDAR' ? (
              <IconCalendar size="xsmall" color="dark" verticalAlign="middle" />
            ) : (
              <IconArrowDown size="xxxsmall" color="blue" />
            )}
          </div>
        </button>
      </div>
      <div className={cn.menuDropdown} role="listbox" ref={itemsWrapperRef}>
        {links.map((link: IMenuListLink, index: number) => {
          if (!link.isSelected || (link.isSelected && showSelected)) {
            return (
              <MenuItem
                callback={onMenuItemClick}
                href={link && link.href}
                id={index}
                isSelected={link.isSelected}
                key={link.label + link.href}
                label={link.label}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default MenuList;
